import React, { useEffect, useMemo, useState } from 'react';
import { ClienteConsolidado } from '../types';
import type { RecorteDaTela } from '../lib/perfisExportacao';
import { formatCurrency, differenceInDays, parseISO } from '../utils';
import { recebeuTodosOsAvisos, TIPOS_AVISO } from '../hooks/useInadimplenciaData';
import { avisoEnviado, avisoFicouComHumano } from '../lib/reguaAvisos';
import { STATUS_NEGOCIACAO_META } from '../../../../src/components/PainelNegociacaoCobranca';
import { useCpfsDosClientes, useEsteiraCobranca } from '../hooks/useEsteiraCobranca';
import { Search, ExternalLink, CalendarClock, BellRing, UserCog, AlertTriangle } from 'lucide-react';

/** Piso da aba: abaixo disso a cobrança ainda está na régua automática. */
const DIAS_LIMITE = 15;

/**
 * Linhas renderizadas por vez. Sem o filtro dos 3 avisos a lista passou de ~180
 * para ~840 faturas, e montar tudo de uma vez trava a rolagem em máquina fraca.
 */
const PAGINA = 200;

type FaixaAtraso = '15' | '30' | '60' | '90';
type FiltroAviso = 'todos' | 'completos' | 'incompletos';

interface Props {
  clientes: ClienteConsolidado[];
  avisosPorFatura: Map<string, Set<string>>;
  onNavigate?: (tab: string, clientName?: string) => void;
  onAbrirGerenciamento?: (cliente: ClienteConsolidado) => void;
  /** Reporta ao cabeçalho o que esta aba está mostrando, para o Excel sair igual. */
  onRecorteChange?: (recorte: RecorteDaTela) => void;
}

const Vencidas15Dias: React.FC<Props> = ({ clientes, avisosPorFatura, onNavigate, onAbrirGerenciamento, onRecorteChange }) => {
  const [busca, setBusca] = useState('');
  const [faixa, setFaixa] = useState<FaixaAtraso>('15');
  const [filtroAviso, setFiltroAviso] = useState<FiltroAviso>('todos');
  const [limite, setLimite] = useState(PAGINA);

  // Universo da aba: toda fatura vencida há DIAS_LIMITE+ dias.
  //
  // ⚠️ Até 01/09/2026 esta lista exigia TAMBÉM os 3 avisos automáticos, e era
  // esse `&& avisosCompletos` que sumia com a cobrança da tela. Quando a régua
  // automática falha — e ela ficou 21 dias fora do ar por um 401 no cron, ver
  // supabase/config.toml em [functions.lembrete-cobranca] — o cliente vencido
  // simplesmente não existia aqui: 841 faturas vencidas há 15+ dias
  // (R$ 658.836,16) contra 180 com os 3 avisos, medido em 01/09/2026. O
  // operador via o valor no cabeçalho e a tabela vazia, sem nem o botão de
  // registrar a negociação. A régua agora é uma COLUNA, não um filtro: falha de
  // automação não pode esconder dívida de quem está atrasado.
  const faturas = useMemo(() => clientes.flatMap(c => c.faturas.map(f => {
    const venc = f.vencimento_boleto || f.data_vencimento;
    const avisosEnviados = avisosPorFatura.get(String(f.id)) || new Set<string>();
    return {
      ...f,
      cliente: c.nome,
      clienteObj: c,
      interno: c.interno,
      venc,
      atraso: venc ? differenceInDays(new Date(), parseISO(venc)) : 0,
      avisosEnviados,
      avisosCompletos: recebeuTodosOsAvisos(avisosEnviados),
    };
  })).filter(f => f.atraso >= DIAS_LIMITE), [clientes, avisosPorFatura]);

  const visiveis = useMemo(() => faturas.filter(f => {
    if (f.atraso < Number(faixa)) return false;
    if (filtroAviso === 'completos' && !f.avisosCompletos) return false;
    if (filtroAviso === 'incompletos' && f.avisosCompletos) return false;
    if (busca) {
      const b = busca.toLowerCase();
      const soDigitos = busca.replace(/\D/g, '');
      if (!f.cliente.toLowerCase().includes(b)
        && !(f.uc_codigo || '').includes(busca)
        && !(soDigitos && (f.clienteObj.cpfCnpj || '').includes(soDigitos))) return false;
    }
    return true;
  }).sort((a, b) => b.atraso - a.atraso), [faturas, faixa, filtroAviso, busca]);

  // O botão "Exportar Excel" fica no cabeçalho da Central, fora desta aba. Sem
  // isto ele não sabe que a lista está cortada em 30+ dias ou só nos sem aviso,
  // e a planilha sairia com as 841 faturas enquanto a tela mostra 180.
  const recorte: RecorteDaTela = useMemo(() => {
    const partes: string[] = [];
    if (faixa !== '15') partes.push(`atraso de ${faixa}+ dias`);
    if (filtroAviso === 'completos') partes.push('só quem recebeu os 3 avisos');
    if (filtroAviso === 'incompletos') partes.push('só quem ficou sem algum aviso');
    if (busca.trim()) partes.push(`busca "${busca.trim()}"`);
    return {
      ids: visiveis.map(f => String(f.id)),
      descricao: partes.join(' · '),
      totalSemFiltro: faturas.length,
    };
  }, [visiveis, faturas.length, faixa, filtroAviso, busca]);

  useEffect(() => { onRecorteChange?.(recorte); }, [recorte, onRecorteChange]);

  // Trocar filtro volta para a primeira página — senão o operador filtra e
  // continua vendo a lista longa anterior.
  const trocarFaixa = (v: string) => { setFaixa(v as FaixaAtraso); setLimite(PAGINA); };
  const trocarFiltroAviso = (v: string) => { setFiltroAviso(v as FiltroAviso); setLimite(PAGINA); };
  const trocarBusca = (v: string) => { setBusca(v); setLimite(PAGINA); };

  // A esteira é buscada só para quem está na tela — o status da negociação
  // evita abrir o gerenciamento cliente a cliente só para ver em que pé está.
  const clientesVisiveis = useMemo(() => {
    const vistos = new Map<string, ClienteConsolidado>();
    visiveis.forEach(f => { if (!vistos.has(f.cliente)) vistos.set(f.cliente, f.clienteObj); });
    return Array.from(vistos.values());
  }, [visiveis]);
  const cpfs = useCpfsDosClientes(clientesVisiveis);
  const { esteira } = useEsteiraCobranca(cpfs);

  const resumo = useMemo(() => {
    const total = visiveis.reduce((s, f) => s + (f.valor_boleto_vigor || 0), 0);
    const semAvisos = visiveis.filter(f => !f.avisosCompletos).length;
    const valorSemAvisos = visiveis
      .filter(f => !f.avisosCompletos)
      .reduce((s, f) => s + (f.valor_boleto_vigor || 0), 0);
    return { total, semAvisos, valorSemAvisos, clientes: clientesVisiveis.length };
  }, [visiveis, clientesVisiveis]);

  return (
    <div className="p-6 h-full flex flex-col">
      <div className="flex items-start justify-between gap-4 mb-4 flex-shrink-0">
        <div>
          <h2 className="text-xl font-bold text-gray-800 flex items-center gap-2">
            <BellRing size={20} className="text-red-500" />
            Vencidas há {DIAS_LIMITE} dias ou mais
          </h2>
          <p className="text-sm text-gray-500 max-w-3xl">
            Toda cobrança vencida há {DIAS_LIMITE}+ dias, tenha ou não recebido os avisos automáticos
            (antes do vencimento, D+3 e D+7). Use <strong>Gerenciar</strong> para registrar ligação, negociação e
            retorno. A fila de contato humano continua em Financeiro › Lembretes de Cobrança ›
            Contato humano.
          </p>
        </div>
        <div className="flex items-center gap-3 flex-shrink-0">
          <div className="text-right">
            <p className="text-[10px] font-black uppercase text-gray-400">Exposição</p>
            <p className="text-sm font-black text-red-600">{formatCurrency(resumo.total)}</p>
            <p className="text-[10px] text-gray-400">{visiveis.length} faturas · {resumo.clientes} clientes</p>
          </div>
          <div className="relative w-64">
            <input
              type="text"
              placeholder="Buscar cliente, UC ou CPF/CNPJ..."
              value={busca}
              onChange={(e) => trocarBusca(e.target.value)}
              className="w-full pl-9 pr-3 py-2 text-xs border border-gray-300 rounded-lg focus:ring-[#5F6C37] focus:border-[#5F6C37]"
            />
            <Search size={14} className="absolute left-3 top-2.5 text-gray-400" />
          </div>
        </div>
      </div>

      <div className="flex flex-wrap items-center gap-2 mb-3 flex-shrink-0">
        <GrupoFiltro rotulo="Atraso" valor={faixa} onChange={trocarFaixa} opcoes={[
          { v: '15', t: '15+ dias' }, { v: '30', t: '30+ dias' },
          { v: '60', t: '60+ dias' }, { v: '90', t: '90+ dias' },
        ]} />
        <GrupoFiltro rotulo="Avisos" valor={filtroAviso} onChange={trocarFiltroAviso} opcoes={[
          { v: 'todos', t: 'Todos' }, { v: 'completos', t: '3 de 3 enviados' }, { v: 'incompletos', t: 'Faltou aviso' },
        ]} />
      </div>

      {resumo.semAvisos > 0 ? (
        <div className="mb-3 px-3 py-2 rounded-lg bg-amber-50 border border-amber-200 text-[11px] text-amber-800 flex items-start gap-2 flex-shrink-0">
          <AlertTriangle size={14} className="mt-0.5 flex-shrink-0" />
          <span>
            <strong>{resumo.semAvisos} cobrança(s)</strong> ({formatCurrency(resumo.valorSemAvisos)}) chegaram
            até aqui sem os 3 avisos automáticos — a régua não alcançou (UC sem WhatsApp cadastrado, boleto
            reemitido ou falha no disparo). Elas continuam listadas: o contato precisa ser feito à mão.
          </span>
        </div>
      ) : null}

      <div className="flex-1 bg-white border border-gray-200 rounded-xl overflow-hidden flex flex-col">
        <div className="overflow-x-auto flex-1">
          <table className="w-full text-sm text-left">
            <thead className="text-xs text-gray-500 uppercase bg-gray-50 sticky top-0 z-10 border-b border-gray-200">
              <tr>
                <th className="px-4 py-3 font-bold">Cliente</th>
                <th className="px-4 py-3 font-bold">UC</th>
                <th className="px-4 py-3 font-bold text-right">Valor</th>
                <th className="px-4 py-3 font-bold">Vencimento</th>
                <th className="px-4 py-3 font-bold">Dias em Atraso</th>
                <th className="px-4 py-3 font-bold">Avisos Enviados</th>
                <th className="px-4 py-3 font-bold">Negociação</th>
                <th className="px-4 py-3 font-bold text-center">Ações</th>
              </tr>
            </thead>
            <tbody>
              {visiveis.length === 0 ? (
                <tr>
                  <td colSpan={8} className="px-4 py-10 text-center text-gray-500">
                    Nenhuma cobrança vencida há {faixa}+ dias nos filtros atuais.
                  </td>
                </tr>
              ) : (
                visiveis.slice(0, limite).map(f => {
                  const reg = f.clienteObj.cpfCnpj ? esteira.get(f.clienteObj.cpfCnpj) : undefined;
                  const meta = reg ? STATUS_NEGOCIACAO_META[reg.status_negociacao] : null;
                  return (
                    <tr key={f.id} className={`border-b border-gray-100 hover:bg-gray-50 ${f.interno ? 'opacity-60' : ''}`}>
                      <td className="px-4 py-3 font-semibold text-gray-800">
                        {f.cliente}
                        {f.interno ? <span className="ml-2 text-[10px] text-gray-400 font-normal border border-gray-200 px-1 rounded">INTERNO</span> : null}
                        {!f.clienteObj.cpfCnpj ? <span className="ml-2 text-[10px] text-red-600 font-normal border border-red-200 px-1 rounded">SEM CPF/CNPJ</span> : null}
                      </td>
                      <td className="px-4 py-3 text-gray-600 text-xs">{f.uc_codigo || '—'}</td>
                      <td className="px-4 py-3 text-right font-black text-[#5F6C37]">{formatCurrency(f.valor_boleto_vigor)}</td>
                      <td className="px-4 py-3 text-xs text-gray-600">
                        <div className="flex items-center gap-1.5">
                          <CalendarClock size={12} className="text-gray-400" />
                          {f.venc ? parseISO(f.venc).toLocaleDateString('pt-BR') : '—'}
                        </div>
                      </td>
                      <td className="px-4 py-3">
                        <span className={`px-2 py-0.5 rounded-full text-[10px] font-bold ${f.atraso >= 60 ? 'bg-red-200 text-red-900' : 'bg-red-100 text-red-700'}`}>
                          {f.atraso}d
                        </span>
                      </td>
                      <td className="px-4 py-3">
                        <ChipsAviso enviados={f.avisosEnviados} completos={f.avisosCompletos} />
                      </td>
                      <td className="px-4 py-3">
                        {meta ? (
                          <div className="flex flex-col gap-1">
                            <span className={`self-start px-2 py-0.5 rounded-full text-[10px] font-bold ${meta.classes}`}>
                              {meta.label}
                            </span>
                            {reg?.proximo_contato_em ? (
                              <span className="text-[10px] text-gray-500">
                                retorno {new Date(reg.proximo_contato_em + 'T00:00:00').toLocaleDateString('pt-BR')}
                              </span>
                            ) : null}
                          </div>
                        ) : (
                          <span className="text-[10px] text-gray-400 italic">sem registro</span>
                        )}
                      </td>
                      <td className="px-4 py-3 text-center">
                        <div className="flex items-center justify-center gap-1">
                          <button
                            title="Detalhes da Fatura (Perfil 360)"
                            onClick={() => onNavigate?.('perfil', f.cliente)}
                            className="text-[#F6A937] hover:text-orange-500 transition-colors p-1 rounded hover:bg-orange-50"
                          >
                            <ExternalLink size={16} />
                          </button>
                          {onAbrirGerenciamento ? (
                            <button
                              title={f.clienteObj.cpfCnpj ? 'Gerenciar cobrança e registrar negociação' : 'Sem CPF/CNPJ — cadastre na UC'}
                              disabled={!f.clienteObj.cpfCnpj}
                              onClick={() => onAbrirGerenciamento(f.clienteObj)}
                              className="text-[#5F6C37] hover:text-[#4a5429] transition-colors p-1 rounded hover:bg-green-50 disabled:opacity-30 disabled:cursor-not-allowed"
                            >
                              <UserCog size={16} />
                            </button>
                          ) : null}
                        </div>
                      </td>
                    </tr>
                  );
                })
              )}
            </tbody>
          </table>
        </div>
        {visiveis.length > limite ? (
          <div className="border-t border-gray-100 px-4 py-3 flex items-center justify-center gap-3 flex-shrink-0">
            <span className="text-[11px] text-gray-500">
              Mostrando {limite} de {visiveis.length} cobranças
            </span>
            <button
              onClick={() => setLimite(l => l + PAGINA)}
              className="px-3 py-1.5 rounded-lg bg-[#5F6C37] hover:bg-[#4a5429] text-white text-[11px] font-semibold transition-colors"
            >
              Carregar mais
            </button>
          </div>
        ) : null}
      </div>
    </div>
  );
};

/**
 * Mostra QUAIS avisos saíram, não só se saíram todos. Cada passo aceita os
 * rótulos das réguas antigas (D-1 antes do vencimento, D+10 no terceiro) — ver
 * `reguaAvisos.ts`. O D+7 que ficou para o contato humano conta como feito.
 */
const ChipsAviso: React.FC<{ enviados: Set<string>; completos: boolean }> = ({ enviados, completos }) => {
  const passos = TIPOS_AVISO.map(tipo => ({
    tipo,
    enviado: avisoEnviado(enviados, tipo),
    humano: avisoFicouComHumano(enviados, tipo),
  }));
  return (
    <div className="flex items-center gap-1">
      {passos.map(({ tipo, enviado, humano }) => (
        <span
          key={tipo}
          title={humano ? `${tipo} não saiu: já tinha contato humano` : enviado ? `${tipo} enviado` : `${tipo} não enviado`}
          className={`px-1.5 py-0.5 rounded text-[9px] font-bold border ${
            enviado ? 'bg-green-50 border-green-200 text-green-700' : 'bg-gray-50 border-gray-200 text-gray-400 line-through'
          }`}
        >
          {tipo}
        </span>
      ))}
      <span className={`ml-1 text-[10px] font-bold ${completos ? 'text-green-700' : 'text-amber-600'}`}>
        {passos.filter(p => p.enviado).length}/{passos.length}
      </span>
    </div>
  );
};

const GrupoFiltro: React.FC<{
  rotulo: string; valor: string; onChange: (v: string) => void; opcoes: { v: string; t: string }[];
}> = ({ rotulo, valor, onChange, opcoes }) => (
  <div className="flex items-center gap-1">
    <span className="text-[10px] font-black uppercase text-gray-400">{rotulo}</span>
    <div className="flex rounded-lg border border-gray-200 overflow-hidden">
      {opcoes.map(o => (
        <button
          key={o.v}
          onClick={() => onChange(o.v)}
          className={`px-2.5 py-1 text-[11px] font-semibold transition-colors ${valor === o.v ? 'bg-[#5F6C37] text-white' : 'bg-white text-gray-600 hover:bg-gray-50'}`}
        >
          {o.t}
        </button>
      ))}
    </div>
  </div>
);

export default Vencidas15Dias;
